import { useAtom, useAtomValue } from 'jotai';
import { backend, domain } from 'modules/data';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GetNetSuiteExchangeRates } from 'shared/data/webApp/v1';
import {
    useNetSuiteAttachmentsFieldSettings,
    useNetSuiteExpenseReportExpenseLinesFieldSettings,
    useNetSuiteExpenseReportFieldSettings,
} from 'shared/hooks';
import { netSuiteExpenseLinesState } from 'shared/states';

import { getNetSuiteContext } from '../../../selectors/netsuite/netSuiteSelectors';
import { getContext } from '../../../selectors/pageSelectors';
import { NetSuiteFieldsExpenseReport } from '../data/types';
import { getCustomFieldsTransfer } from '../helpers';
import { useExchangeRates } from '../hooks';
import { isFutureDate } from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { netSuiteCustomFieldsState, netSuiteShowErrorsState } from '../resources/states';
import { checkExpenseLineIsEmpty } from './helpers/checkExpenseLineIsEmpty/checkExpenseLineIsEmpty';
import { getTransferExpenseLine } from './helpers/getTransferExpenseLine/getTransferExpenseLine';
import { validateExpenseReportForm } from './helpers/validateExpenseReportForm/validateExpenseReportForm';
import { useExpenseReportAmount, useExpenseReportEmployeeCurrencies, useExpenseReportFieldValues } from './hooks';
import { netSuiteExpenseReportFieldValuesState } from './NetSuiteExpenseReportRequestForm.states';

export const useExpenseReportForm = (request: domain.NetSuiteExpenseReportRequest) => {
    const editingContext = useSelector(getNetSuiteContext);

    const fieldValues = useAtomValue(netSuiteExpenseReportFieldValuesState);
    const expenseLines = useAtomValue(netSuiteExpenseLinesState);

    const { setFieldValue } = useExpenseReportFieldValues();

    const { documentFields } = editingContext;

    const { data: exchangeRatesData } = useExchangeRates(request.companyId, fieldValues.date);

    const fieldSettings = useNetSuiteExpenseReportFieldSettings(documentFields);
    const currencies = useExpenseReportEmployeeCurrencies(fieldValues.employee);

    const requestCurrencyISOCode = useMemo<string | null>(() => {
        if (fieldValues.currency?.id) {
            const selectedCurrency = currencies.find((currency) => currency.id === fieldValues.currency?.id);

            return selectedCurrency?.ISOCode || null;
        }

        return null;
    }, [currencies, fieldValues.currency]);

    const requestCurrencyRate = useMemo<number | null>(() => {
        if (fieldValues.currency?.id) {
            const currencyRate = exchangeRatesData?.rates.find((rate) => rate.id === fieldValues.currency?.id);

            return currencyRate?.rateValue ?? null;
        }

        return null;
    }, [fieldValues.currency, exchangeRatesData]);

    const onDetailsChange =
        <K extends keyof NetSuiteFieldsExpenseReport>(key: K) =>
        (value: NetSuiteFieldsExpenseReport[K]) => {
            setFieldValue(key, value);
        };

    const onCurrencyChange = useCallback(
        (value: NetSuiteFieldsExpenseReport['currency']) => {
            setFieldValue('currency', value);

            if (value === null) {
                setFieldValue('accountForCorporateCardExpenses', null);
            }
        },
        [setFieldValue]
    );

    return {
        fieldValues,
        fieldSettings,
        expenseLines,
        exchangeRatesData,
        currencies,
        requestCurrencyISOCode,
        requestCurrencyRate,
        onDetailsChange,
        onCurrencyChange,
    };
};

export const useExpenseReportFormSubmit = (
    request: domain.NetSuiteExpenseReportRequest,
    companyCurrency: string,
    requestCurrencyISOCode: string | null,
    exchangeRatesData?: GetNetSuiteExchangeRates['response']
) => {
    const pageContext = useSelector(getContext);
    const editingContext = useSelector(getNetSuiteContext);
    const fieldValues = useAtomValue(netSuiteExpenseReportFieldValuesState);
    const customFieldsValues = useAtomValue(netSuiteCustomFieldsState);
    const expenseLines = useAtomValue(netSuiteExpenseLinesState);

    const [showErrors, setShowErrors] = useAtom(netSuiteShowErrorsState);

    const { documentFields, customFields, currencies } = editingContext;

    const fieldSettings = useNetSuiteExpenseReportFieldSettings(documentFields);
    const fieldsSettingsExpenseLine = useNetSuiteExpenseReportExpenseLinesFieldSettings(documentFields);
    const attachmentsFieldSettings = useNetSuiteAttachmentsFieldSettings(documentFields);
    const { totalExpenses, advanceToApply, totalReimbursableAmount } = useExpenseReportAmount();

    const defaultCurrencyRate = exchangeRatesData?.rates.find((currency) => {
        return currency.id === fieldValues.currency?.id;
    });

    const getValidationErrors = async () => {
        setShowErrors(true);

        return validateExpenseReportForm({
            fieldValues,
            customFieldsValues,
            customFieldsList: customFields,
            workflowId: request.template.id,
            expenseLines,
            totalExpenses,
            advanceToApply,
            totalReimbursableAmount,
            defaultRate: defaultCurrencyRate?.rateValue,
            fieldSettings,
            fieldsSettingsExpenseLine,
            companyCurrency,
            requestCurrencyISOCode,
            attachmentsFieldSettings,
            attachments: request.attachments,
        });
    };

    const getRequestTransfer = (): backend.Transfer<backend.transfers.RequestEditTransfer> => ({
        requestId: request.id,
        amount: totalReimbursableAmount || null,
        currency: currencies.find((currency) => currency.id === fieldValues.currency?.id)?.ISOCode,
        exchangeRate: fieldValues.exchangeRate || defaultCurrencyRate?.rateValue || 1,
        exchangeRateSource:
            typeof fieldValues.exchangeRate === 'number'
                ? domain.ExchangeRateSource.Manual
                : domain.ExchangeRateSource.NetSuite,
        exchangeRateDate: isFutureDate(fieldValues.date)
            ? moment.utc().format('yyyy-MM-DD')
            : moment.utc(fieldValues.date).format('yyyy-MM-DD'),
        attachments: request.attachments.map((attachment) => attachment.id),
        requestNote: request.requestNote.value,
        templateVersion: pageContext.templateVersion,
        companyId: request.companyId,
        netSuiteExpenseReportDetails: {
            accountId: fieldValues.account?.id || null,
            accountForCorporateCardExpensesId: fieldValues.accountForCorporateCardExpenses?.id || null,
            advance: advanceToApply,
            approvalStatus: fieldValues.approvalStatus || null,
            createdDate: fieldValues.createdDate || null,
            currencyId: fieldValues.currency?.id || null,
            date: fieldValues.date || null,
            dueDate: fieldValues.dueDate || null,
            employeeId: fieldValues.employee?.id || null,
            exchangeRate: fieldValues.exchangeRate || defaultCurrencyRate?.rateValue || null,
            internalId: fieldValues.internalId || null,
            modifiedDate: fieldValues.modifiedDate || null,
            postingPeriodId: fieldValues.postingPeriod?.id || null,
            reimbursableAmount: totalReimbursableAmount,
            status: fieldValues.status || null,
            subsidiaryId: fieldValues.subsidiary?.id || null,
            taxAmount: fieldValues.taxAmount || null,
            totalAmount: totalExpenses,
            transactionNumber: fieldValues.transactionNumber || null,
            url: fieldValues.url || null,
            useMultiCurrency: fieldValues.useMultiCurrency || null,
            classId: fieldValues.class?.id || null,
            locationId: fieldValues.location?.id || null,
            departmentId: fieldValues.department?.id || null,
            memo: fieldValues.memo || null,
            expenseLines: expenseLines
                .filter((expenseLine) => !checkExpenseLineIsEmpty(expenseLine))
                .map((expenseLine) => getTransferExpenseLine(expenseLine)),
            customFields: getCustomFieldsTransfer(Object.values(customFieldsValues)),
        },
    });

    return {
        showErrors,
        setShowErrors,
        getValidationErrors,
        getRequestTransfer,
        attachmentsFieldSettings,
    };
};
